import React from 'react'
import { Assessment } from '../assessmentDashboard'
import { getPresignedUrl, inputFiles, uploadToS3 } from '../../../components/media/fileUpload'
import { Button, ButtonShape } from '../../../components/simple/button'
import { makeRequest, showAxiosErrorAsToast } from '../../../request'
import { LocalTable } from '../../../components/table'
import { IconTrash } from '../../../icons/library'
import { Tooltip } from '../../../components/simple/tooltip'
import { TextInput } from '../../../components/form/textInput'
import { useNavigate } from 'react-router-dom'
import { Divider } from '../../../components/simple/divider'
import { InstructionPanel } from '../../../components/simple/instruction'

export const UploadTab: React.FC<{
  assessment: Assessment
  reloadAssessment: () => Promise<void>
}> = ({ assessment, reloadAssessment }) => {
  const [loading, setLoading] = React.useState(false)
  const [loadingIdentifier, setLoadingIdentifier] = React.useState(false)
  const [identifier, setIdentifier] = React.useState(assessment.identifier || '')
  const [identifierMessage, setIdentifierMessage] = React.useState('')
  const [identifierMessageError, setIdentifierMessageError] = React.useState(false)
  const [lastSavedIdentifier, setLastSavedIdentifier] = React.useState(assessment.identifier || '')

  React.useEffect(() => {
    if (assessment.identifier) {
      setIdentifier(assessment.identifier)
      setLastSavedIdentifier(assessment.identifier)
    }
  }, [assessment.identifier])

  const navigate = useNavigate()
  const [search, setSearch] = React.useState('')
  return (
    <div className={`mt-2 flex min-w-[500px] max-w-[700px] flex-col items-start gap-2 self-center`}>
      <InstructionPanel>
        <b>Upload your assessment data here.</b> Allowed formats are PDF with one or more pages per student, or a ZIP
        file containing a PDF for each student. You can upload multiple seperate files, as long as you do not mix ZIP
        and PDF. Every page you want attributed to a specific student must always contain that student's last name.
      </InstructionPanel>
      <Button
        logo
        showLoader={loading}
        disabled={loading}
        onClick={async () => {
          setLoading(true)
          const files = await inputFiles('application/pdf, application/zip')
          let uploaded = 0
          for (const file of files || []) {
            try {
              const url = await getPresignedUrl(file.name)
              await uploadToS3(url, file)
              const predictedIdentifier = await makeRequest<string>('/frontend_api/uploadAssessmentFile', {
                fileName: file.name,
                assessmentID: assessment.id,
              })
              uploaded++
              setIdentifierMessage('')
              if (!identifier.trim() && predictedIdentifier) {
                setIdentifier(predictedIdentifier)
                setIdentifierMessageError(false)
                setIdentifierMessage('Predicted identifier based on your file.')
              }
            } catch (e) {
              showAxiosErrorAsToast(e)
            }
          }
          if (uploaded) reloadAssessment()
          setLoading(false)
        }}
      >
        Upload Files
      </Button>
      {/* This table doesnt really need search, right? If you want it back, just uncomment the next line */}
      {/* <TextInput value={search} onChange={setSearch} placeholder="Search" /> */}
      <LocalTable
        className="self-stretch"
        rowName="file"
        search={search}
        columns={[
          {
            header: 'File Name',
            accessorKey: 'name',
          },
          {
            header: 'Size',
            accessorKey: 'size',
            cell: ({ row }) => <span>{(row.original.size / 1024).toFixed(2)} KB</span>,
          },
          {
            header: 'Remove',
            cell: ({ row }) => (
              <DeleteFileButton
                fileName={row.original.name}
                assessmentID={assessment.id}
                reloadAssessment={reloadAssessment}
              />
            ),
          },
        ]}
        data={assessment.files}
        pagination={5}
      />

      <Divider className="my-4" />

      <Tooltip text={assessment.files.length ? '' : 'No files uploaded yet'}>
        <div className={`${assessment.files.length ? '' : 'opacity-50'} flex flex-col items-start gap-2 self-stretch`}>
          <b>Identifier</b>
          {assessment.files.length > 0 && (
            <InstructionPanel>
              {assessment.files[0].name.toLowerCase().endsWith('.pdf') ? (
                <p>
                  The identifier should match text content inside the PDF. For example{' '}
                  <code>Student: &lt;student_id&gt;</code> would match a file containing the text{' '}
                  <code>Student: 5342341</code> with the student with id 5342341.
                </p>
              ) : (
                <p>
                  The identifier should match filenames of PDF files inside the ZIP. For example,{' '}
                  <code>%_&lt;student_id&gt;.pdf</code> would match a file named <code>AssessmentFile_5342341.pdf</code>{' '}
                  with student id <code>5342341</code>.
                </p>
              )}
            </InstructionPanel>
          )}
          <TextInput
            className="self-stretch"
            value={identifier}
            onChange={setIdentifier}
            placeholder="Match Identifier"
            disabled={!assessment.files.length}
            invalid={!!identifierMessageError}
          />
          {identifierMessage && (
            <div className={`text-sm ${identifierMessageError ? 'text-red-500' : 'text-green-500'}`}>
              {identifierMessage}
            </div>
          )}
          <div className="flex gap-2">
            <Button
              logo={identifier !== lastSavedIdentifier}
              showLoader={loadingIdentifier}
              disabled={!identifier || loadingIdentifier || !assessment.files.length}
              onClick={() => {
                setLoadingIdentifier(true)
                setIdentifierMessage('')
                makeRequest<{ message: string; error: boolean }>('/frontend_api/setAssessmentIdentifier', {
                  assessmentID: assessment.id,
                  identifier: identifier.length ? identifier : undefined,
                })
                  .then(({ message, error }) => {
                    setIdentifierMessageError(error)
                    setIdentifierMessage(message)
                    setLastSavedIdentifier(identifier)
                  })
                  .catch(showAxiosErrorAsToast)
                  .finally(() => setLoadingIdentifier(false))
              }}
            >
              {identifier !== lastSavedIdentifier ? 'Save' : 'Test'}
            </Button>
            {identifier === lastSavedIdentifier && (
              <Button
                logo
                showLoader={loadingIdentifier}
                disabled={!identifier || loadingIdentifier || !assessment.files.length}
                onClick={() =>
                  reloadAssessment().then(() =>
                    navigate(assessment.needsAdditionalData ? '#additionalData' : '#process')
                  )
                }
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      </Tooltip>
    </div>
  )
}

const DeleteFileButton: React.FC<{
  fileName: string
  assessmentID: number
  reloadAssessment: () => void
}> = ({ fileName, assessmentID, reloadAssessment }) => {
  const [loading, setLoading] = React.useState(false)

  return (
    <Button
      logo
      disabled={loading}
      shape={ButtonShape.icon}
      onClick={() => {
        setLoading(true)
        makeRequest('/frontend_api/deleteAssessmentFile', {
          fileName,
          assessmentID,
        })
          .then(() => reloadAssessment())
          .catch(showAxiosErrorAsToast)
          .finally(() => setLoading(false))
      }}
    >
      <IconTrash />
    </Button>
  )
}
